import type { AssessmentFormUiConfig, MaturityOptionUi } from './assessmentFormConfig';
import {
  DEFAULT_ASSESSMENT_FORM_UI_CONFIG,
  splitCommentPlaceholder,
} from './assessmentFormConfig';
import {
  QUESTION_STATE,
  createEmptyQuestion,
  joinQuestionTexts,
  normalizeQuestionList,
  type SubCapabilityQuestionDraft,
} from './subCapabilityQuestionUtils';

export interface SubCapabilityMaturityOption {
  optionValue: number;
  label: string;
  definition?: string;
}

export interface SubCapabilityAssessmentFields {
  currentStateQuestions?: SubCapabilityQuestionDraft[] | null;
  futureStateQuestions?: SubCapabilityQuestionDraft[] | null;
  currentStateCommentPlaceholder?: string | null;
  futureStateCommentPlaceholder?: string | null;
  maturityOptionsJson?: string | null;
  /** UI-only: whether custom maturity scale is being edited */
  useCustomMaturity?: boolean;
  /** UI-only parsed options while editing */
  maturityOptions?: SubCapabilityMaturityOption[];
}

/** Capability and sub-capability nodes share this assessment content shape. */
export type AssessmentMaturityOption = SubCapabilityMaturityOption;
export type AssessmentContentFields = SubCapabilityAssessmentFields;

export const EMPTY_SUB_CAPABILITY = {
  subId: 0,
  capabilityId: 0,
  subCapabilityName: '',
  subCapabilityDescription: '',
  isActive: true,
  currentStateQuestions: [createEmptyQuestion(QUESTION_STATE.current)],
  futureStateQuestions: [createEmptyQuestion(QUESTION_STATE.future)],
  currentStateCommentPlaceholder: '',
  futureStateCommentPlaceholder: '',
  maturityOptionsJson: null as string | null,
  useCustomMaturity: false,
  maturityOptions: DEFAULT_ASSESSMENT_FORM_UI_CONFIG.maturityOptions.map((option) => ({
    optionValue: option.optionValue,
    label: option.label,
    definition: option.definition,
  })),
};

export function cloneDefaultMaturityOptions(): SubCapabilityMaturityOption[] {
  return DEFAULT_ASSESSMENT_FORM_UI_CONFIG.maturityOptions.map((option) => ({
    optionValue: option.optionValue,
    label: option.label,
    definition: option.definition,
  }));
}

/** Shared leaf/sub-capability assessment copy used by admin forms. */
export function normalizeAssessmentFieldsFromApi(source?: SubCapabilityAssessmentFields | null) {
  const parsed = parseMaturityOptionsJson(source?.maturityOptionsJson);
  return {
    currentStateCommentPlaceholder: source?.currentStateCommentPlaceholder ?? '',
    futureStateCommentPlaceholder: source?.futureStateCommentPlaceholder ?? '',
    maturityOptionsJson: source?.maturityOptionsJson ?? null,
    useCustomMaturity: Boolean(parsed?.length),
    maturityOptions: parsed?.length ? parsed : cloneDefaultMaturityOptions(),
  };
}

export function emptyAssessmentFields() {
  return {
    currentStateCommentPlaceholder: '',
    futureStateCommentPlaceholder: '',
    maturityOptionsJson: null as string | null,
    useCustomMaturity: false,
    maturityOptions: cloneDefaultMaturityOptions(),
  };
}

export function createEmptySubCapability(capabilityId = 0) {
  return {
    ...EMPTY_SUB_CAPABILITY,
    capabilityId,
    currentStateQuestions: [createEmptyQuestion(QUESTION_STATE.current)],
    futureStateQuestions: [createEmptyQuestion(QUESTION_STATE.future)],
    maturityOptions: cloneDefaultMaturityOptions(),
  };
}

export function parseMaturityOptionsJson(
  json?: string | null
): SubCapabilityMaturityOption[] | null {
  if (!json || !json.trim()) {
    return null;
  }

  try {
    const parsed = JSON.parse(json);
    if (!Array.isArray(parsed) || parsed.length === 0) {
      return null;
    }

    return parsed
      .map((item, index) => ({
        optionValue: Number(item.optionValue ?? item.value ?? index + 1),
        label: String(item.label ?? '').trim(),
        definition: item.definition ? String(item.definition) : '',
      }))
      .filter((item) => item.label.length > 0);
  } catch {
    return null;
  }
}

export function serializeMaturityOptions(
  options: SubCapabilityMaturityOption[] | null | undefined,
  useCustomMaturity: boolean
): string | null {
  if (!useCustomMaturity || !options?.length) {
    return null;
  }

  return JSON.stringify(
    options.map((option, index) => ({
      optionValue: option.optionValue || index + 1,
      label: option.label.trim(),
      definition: option.definition?.trim() || '',
    }))
  );
}

export function normalizeSubCapabilityFromApi(subCap: any, capabilityId = 0) {
  const currentStateQuestions = normalizeQuestionList(
    subCap?.currentStateQuestions ?? subCap?.CurrentStateQuestions,
    QUESTION_STATE.current
  );
  const futureStateQuestions = normalizeQuestionList(
    subCap?.futureStateQuestions ?? subCap?.FutureStateQuestions,
    QUESTION_STATE.future
  );

  return {
    subId: subCap?.subId ?? subCap?.id ?? 0,
    capabilityId: subCap?.capabilityId ?? capabilityId,
    subCapabilityName: subCap?.subCapabilityName ?? subCap?.name ?? '',
    subCapabilityDescription: subCap?.subCapabilityDescription ?? subCap?.description ?? '',
    isActive: subCap?.isActive !== undefined ? Boolean(subCap.isActive) : true,
    currentStateQuestions: currentStateQuestions.length
      ? currentStateQuestions
      : [createEmptyQuestion(QUESTION_STATE.current)],
    futureStateQuestions: futureStateQuestions.length
      ? futureStateQuestions
      : [createEmptyQuestion(QUESTION_STATE.future)],
    ...normalizeAssessmentFieldsFromApi(subCap),
  };
}

function toMaturityOptionUi(options: SubCapabilityMaturityOption[]): MaturityOptionUi[] {
  return options.map((option) => {
    const label = option.label.trim();
    const key = label.toLowerCase().replace(/\s+/g, '-') || `option-${option.optionValue}`;
    return {
      key,
      label,
      optionValue: option.optionValue,
      definition: option.definition ?? '',
    };
  });
}

/**
 * Builds assessment UI config from per-sub-capability nullable fields.
 * Any missing field falls back to the legacy hardcoded defaults.
 */
export function buildFormConfigFromSubCapability(
  subCap?: SubCapabilityAssessmentFields | null
): AssessmentFormUiConfig {
  const defaults = DEFAULT_ASSESSMENT_FORM_UI_CONFIG;
  const parsedOptions =
    subCap?.maturityOptions?.length
      ? subCap.maturityOptions
      : parseMaturityOptionsJson(subCap?.maturityOptionsJson);

  const maturityOptions = parsedOptions?.length
    ? toMaturityOptionUi(parsedOptions)
    : defaults.maturityOptions;

  const currentQuestion = joinQuestionTexts(subCap?.currentStateQuestions);
  const futureQuestion = joinQuestionTexts(subCap?.futureStateQuestions);
  const currentPlaceholder = subCap?.currentStateCommentPlaceholder?.trim();
  const futurePlaceholder = subCap?.futureStateCommentPlaceholder?.trim();

  return {
    ...defaults,
    maturityOptions,
    maturityStateKeys: maturityOptions.map((option) => option.key),
    stateDescriptions: maturityOptions.reduce<Record<string, string>>((map, option) => {
      map[option.key] = option.definition || defaults.stateDescriptions[option.key] || '';
      return map;
    }, {}),
    currentSection: {
      title: defaults.currentSection.title,
      description: currentQuestion || defaults.currentSection.description,
      ...(currentPlaceholder
        ? splitCommentPlaceholder(currentPlaceholder)
        : {
            commentPlaceholder: defaults.currentSection.commentPlaceholder,
            commentExample: defaults.currentSection.commentExample,
          }),
    },
    futureSection: {
      title: defaults.futureSection.title,
      description: futureQuestion || defaults.futureSection.description,
      ...(futurePlaceholder
        ? splitCommentPlaceholder(futurePlaceholder)
        : {
            commentPlaceholder: defaults.futureSection.commentPlaceholder,
            commentExample: defaults.futureSection.commentExample,
          }),
    },
  };
}

export function withCustomMaturityToggle<T extends SubCapabilityAssessmentFields>(
  entity: T,
  enabled: boolean
): T {
  if (!enabled) {
    return {
      ...entity,
      useCustomMaturity: false,
      maturityOptions: cloneDefaultMaturityOptions(),
      maturityOptionsJson: null,
    };
  }

  return {
    ...entity,
    useCustomMaturity: true,
    maturityOptionsJson: serializeMaturityOptions(
      entity.maturityOptions || cloneDefaultMaturityOptions(),
      true
    ),
  };
}

export function withMaturityOptionPatch<T extends SubCapabilityAssessmentFields>(
  entity: T,
  optionIndex: number,
  patch: Partial<SubCapabilityMaturityOption>
): T {
  const maturityOptions = [...(entity.maturityOptions || [])];
  maturityOptions[optionIndex] = { ...maturityOptions[optionIndex], ...patch };
  return {
    ...entity,
    maturityOptions,
    useCustomMaturity: true,
    maturityOptionsJson: serializeMaturityOptions(maturityOptions, true),
  };
}

export function withAddedMaturityOption<T extends SubCapabilityAssessmentFields>(entity: T): T {
  const maturityOptions = addMaturityOption(entity.maturityOptions || []);
  return {
    ...entity,
    maturityOptions,
    useCustomMaturity: true,
    maturityOptionsJson: serializeMaturityOptions(maturityOptions, true),
  };
}

export function withRemovedMaturityOption<T extends SubCapabilityAssessmentFields>(
  entity: T,
  optionIndex: number
): T {
  const maturityOptions = removeMaturityOption(entity.maturityOptions || [], optionIndex);
  return {
    ...entity,
    maturityOptions,
    useCustomMaturity: true,
    maturityOptionsJson: serializeMaturityOptions(maturityOptions, true),
  };
}

export function addMaturityOption(options: SubCapabilityMaturityOption[]): SubCapabilityMaturityOption[] {
  const nextValue = options.reduce((max, option) => Math.max(max, option.optionValue), 0) + 1;
  return [
    ...options,
    {
      optionValue: nextValue,
      label: `Level ${nextValue}`,
      definition: '',
    },
  ];
}

export function removeMaturityOption(
  options: SubCapabilityMaturityOption[],
  index: number
): SubCapabilityMaturityOption[] {
  if (options.length <= 1) {
    return options;
  }
  return options
    .filter((_, i) => i !== index)
    .map((option, i) => ({ ...option, optionValue: option.optionValue || i + 1 }));
}
